// =====================================================
// PANEL DE ALERTAS METEOROLÓGICAS OFICIALES
// src/pages/Agro/Alertas/PanelAlertas.js
//
// Dos modos, misma tarjeta:
//   alcance="cartera"          -> todas las alertas vigentes. Cada una se
//                                 despliega y muestra qué establecimientos y
//                                 productores toca. Va en el dashboard.
//   alcance="establecimiento"  -> solo las que caen sobre ese establecimiento.
//                                 Va en la pestaña Clima de la ficha.
//
// Fuente: feeds CAP del SMN (ssl.smn.gob.ar), licencia CC BY 4.0. La
// atribución al pie es obligación de la licencia, no decoración.
// =====================================================

import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Badge, Spinner, Alert, Table, Collapse } from "reactstrap"
import { Link } from "react-router-dom"
import dayjs from "dayjs"

import { getAlertasVigentes, getAlertasEstablecimiento } from "api/agro"

// Escala interna -> color de reactstrap. Espeja los colores del SAT del SMN:
// rojo / naranja / amarillo.
const COLOR_SEVERIDAD = {
  CRITICO: "danger",
  ALTO: "warning",
  MEDIO: "warning",
  BAJO: "info",
}

const ICONO_TIPO = {
  ZONDA: "mdi-weather-windy",
  VIENTO: "mdi-weather-windy",
  GRANIZO: "mdi-weather-hail",
  TORMENTA: "mdi-weather-lightning-rainy",
  LLUVIAS_INTENSAS: "mdi-weather-pouring",
  NEVADA: "mdi-weather-snowy-heavy",
  HELADA: "mdi-snowflake",
  TEMP_EXTREMA_CALOR: "mdi-thermometer-high",
  TEMP_EXTREMA_FRIO: "mdi-thermometer-low",
  TEMP_EXTREMA: "mdi-thermometer",
  CENIZA_VOLCANICA: "mdi-volcano",
  SEQUIA: "mdi-water-off",
  EXCESO_HIDRICO: "mdi-home-flood",
  EMERGENCIA_AGRO: "mdi-file-document-alert",
}

const ETIQUETA_TIPO = {
  ZONDA: "Viento Zonda",
  VIENTO: "Viento",
  GRANIZO: "Granizo",
  TORMENTA: "Tormentas",
  LLUVIAS_INTENSAS: "Lluvias intensas",
  NEVADA: "Nevadas",
  HELADA: "Helada",
  TEMP_EXTREMA_CALOR: "Temperaturas extremas — calor",
  TEMP_EXTREMA_FRIO: "Temperaturas extremas — frío",
  TEMP_EXTREMA: "Temperaturas extremas",
  CENIZA_VOLCANICA: "Ceniza volcánica",
  SEQUIA: "Sequía",
  EXCESO_HIDRICO: "Exceso hídrico",
  EMERGENCIA_AGRO: "Emergencia agropecuaria",
  METEOROLOGICA: "Alerta meteorológica",
}

const vigencia = alerta => {
  const desde = alerta.fechaInicio ? dayjs(alerta.fechaInicio) : null
  const hasta = alerta.vigenteHasta
    ? dayjs(alerta.vigenteHasta)
    : alerta.fechaFin
      ? dayjs(alerta.fechaFin)
      : null

  if (desde && hasta) {
    return desde.isSame(hasta, "day")
      ? `${desde.format("DD/MM")} · hasta ${hasta.format("HH:mm")}`
      : `${desde.format("DD/MM HH:mm")} — ${hasta.format("DD/MM HH:mm")}`
  }
  if (hasta) return `Hasta ${hasta.format("DD/MM HH:mm")}`
  if (desde) return `Desde ${desde.format("DD/MM HH:mm")}`
  return null
}

const ha = valor =>
  valor == null ? null : `${Number(valor).toLocaleString("es-AR")} ha`

const PanelAlertas = ({
  alcance,
  idEstablecimiento,
  limite,
  ocultarSiVacio,
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [alertas, setAlertas] = useState([])
  const [atribucion, setAtribucion] = useState(null)
  const [expandida, setExpandida] = useState(null)
  const [verTodas, setVerTodas] = useState(false)

  useEffect(() => {
    let vivo = true

    const cargar = async () => {
      setLoading(true)
      setError(null)
      try {
        if (alcance === "establecimiento") {
          if (!idEstablecimiento) {
            if (vivo) setAlertas([])
            return
          }
          const data = await getAlertasEstablecimiento(idEstablecimiento)
          if (vivo) setAlertas(Array.isArray(data) ? data : [])
        } else {
          const data = await getAlertasVigentes()
          if (vivo) {
            setAlertas(Array.isArray(data?.alertas) ? data.alertas : [])
            setAtribucion(data?.atribucion || null)
          }
        }
      } catch (e) {
        // "Sin alertas" y "no pudimos consultar" son cosas distintas: un panel
        // vacío por error de red no puede leerse como "no hay riesgo".
        console.warn("Error cargando alertas:", e)
        if (vivo) setError("No se pudieron cargar las alertas meteorológicas")
      } finally {
        if (vivo) setLoading(false)
      }
    }

    cargar()
    return () => {
      vivo = false
    }
  }, [alcance, idEstablecimiento])

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner size="sm" color="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert color="warning" className="mb-0">
        <i className="mdi mdi-alert-outline me-2" />
        {error}
      </Alert>
    )
  }

  if (!alertas.length) {
    if (ocultarSiVacio) return null

    return (
      <div className="text-center text-muted py-3">
        <i
          className="mdi mdi-shield-check-outline d-block mb-2 text-success"
          style={{ fontSize: 28 }}
        />
        <small>
          {alcance === "establecimiento"
            ? "Sin alertas meteorológicas vigentes para este establecimiento"
            : "Sin alertas meteorológicas vigentes sobre la cartera"}
        </small>
      </div>
    )
  }

  const visibles = limite && !verTodas ? alertas.slice(0, limite) : alertas
  const ocultas = alertas.length - visibles.length

  return (
    <>
      {visibles.map((a, i) => {
        const color = COLOR_SEVERIDAD[a.nivelSeveridad] || "warning"
        const clave = a.capIdentifier || `manual-${i}`
        const abierta = expandida === clave
        const rango = vigencia(a)
        const detallable =
          alcance !== "establecimiento" && a.cantidadEstablecimientos > 0

        return (
          <div
            key={clave}
            className={`p-3 mb-2 rounded border-start border-4 border-${color}`}
            style={{ backgroundColor: "var(--bs-light, #f8f9fa)" }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex">
                <div className="me-3 mt-1">
                  <i
                    className={`mdi ${ICONO_TIPO[a.tipo] || "mdi-alert-outline"} text-${color}`}
                    style={{ fontSize: "1.4rem" }}
                  />
                </div>
                <div>
                  <div className="d-flex align-items-center flex-wrap mb-1">
                    <Badge
                      color={color}
                      className="me-2"
                      style={{ fontSize: "0.7rem" }}
                    >
                      {a.nivelSeveridad}
                    </Badge>
                    <strong className="me-2">
                      {ETIQUETA_TIPO[a.tipo] || a.tipo}
                    </strong>
                    {a.esEmergenciaOficial && (
                      <Badge color="dark" style={{ fontSize: "0.65rem" }}>
                        Emergencia oficial
                        {a.numeroResolucion ? ` · ${a.numeroResolucion}` : ""}
                      </Badge>
                    )}
                  </div>

                  {rango && (
                    <div
                      className="text-muted mb-1"
                      style={{ fontSize: "0.75rem" }}
                    >
                      <i className="mdi mdi-clock-outline me-1" />
                      {rango}
                    </div>
                  )}

                  <p className="mb-0 text-muted small">{a.descripcion}</p>

                  {detallable && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 mt-2 text-decoration-none"
                      onClick={() => setExpandida(abierta ? null : clave)}
                    >
                      <i
                        className={`mdi ${abierta ? "mdi-chevron-up" : "mdi-chevron-down"} me-1`}
                      />
                      {a.cantidadEstablecimientos} establecimiento
                      {a.cantidadEstablecimientos === 1 ? "" : "s"}
                      {a.cantidadProductores > 0 &&
                        ` · ${a.cantidadProductores} productor${
                          a.cantidadProductores === 1 ? "" : "es"
                        }`}
                      {a.superficieAfectadaHa > 0 &&
                        ` · ${ha(a.superficieAfectadaHa)}`}
                    </button>
                  )}
                </div>
              </div>

              {a.urlOrigen && (
                <a
                  href={a.urlOrigen}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted ms-2"
                  title="Ver el documento CAP original del SMN"
                >
                  <i className="mdi mdi-open-in-new" />
                </a>
              )}
            </div>

            {detallable && (
              <Collapse isOpen={abierta}>
                <div className="mt-3">
                  <Table size="sm" borderless className="mb-0">
                    <thead>
                      <tr className="text-muted" style={{ fontSize: "0.7rem" }}>
                        <th>Establecimiento</th>
                        <th>Productor</th>
                        <th>Ubicación</th>
                        <th className="text-end">Superficie</th>
                      </tr>
                    </thead>
                    <tbody style={{ fontSize: "0.8rem" }}>
                      {(a.establecimientos || []).map(e => (
                        <tr key={e.idAlerta || e.idEstablecimiento}>
                          <td>{e.nombre}</td>
                          <td>
                            {e.idProductor ? (
                              <Link to={`/agro/productor/${e.idProductor}`}>
                                {e.productor || "—"}
                              </Link>
                            ) : (
                              e.productor || "—"
                            )}
                          </td>
                          <td className="text-muted">
                            {[e.localidad, e.provincia]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </td>
                          <td className="text-end">
                            {ha(e.superficieTotalHa) || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Collapse>
            )}
          </div>
        )
      })}

      {ocultas > 0 && (
        <div className="text-center mt-2">
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={() => setVerTodas(true)}
          >
            <i className="mdi mdi-eye-outline me-1" />
            Ver las {alertas.length} alertas
          </button>
        </div>
      )}

      {atribucion && (
        <div
          className="text-muted text-end mt-2"
          style={{ fontSize: "0.7rem" }}
        >
          {atribucion}
        </div>
      )}
    </>
  )
}

PanelAlertas.propTypes = {
  alcance: PropTypes.oneOf(["cartera", "establecimiento"]),
  idEstablecimiento: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Muestra solo las N primeras, con un "ver todas" al pie. */
  limite: PropTypes.number,
  /** No renderiza nada si no hay alertas, en lugar del cartel "sin alertas". */
  ocultarSiVacio: PropTypes.bool,
}

PanelAlertas.defaultProps = {
  alcance: "cartera",
  ocultarSiVacio: false,
}

export default PanelAlertas
